import React, { useState, useRef, useEffect, useCallback } from 'react';
import './FieldVideo.css';
import useFileDrop from '../hooks/useFileDrop';

// Video clips attached to one field of a location. Deliberately quiet in the
// layout: the column holds one chip per clip, stacked, plus an add button while
// the field is under its cap; the player itself unfolds below the whole media
// row — see FieldVideoPlayer. Which clip is open lives in the parent because
// the two parts are rendered in different places.

const MAX_UPLOAD_MB = 300;
// Must match MAX_VIDEOS_PER_FIELD on the server.
const DEFAULT_MAX_VIDEOS = 5;

const formatBytes = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
};

const formatDuration = (sec) => {
  if (!sec && sec !== 0) return '';
  const total = Math.round(sec);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * The clip itself, shown full width under the media row — a third of the row
 * would be too small to judge footage by.
 */
export const FieldVideoPlayer = ({ video }) => (
  <figure className="field-video-figure">
    <video
      // Replacing a clip keeps the row id, so the src string doesn't change and
      // React would leave the element (and the old footage) in place. Keying on
      // the upload time remounts the player on a replacement.
      key={video.uploaded_at}
      className="field-video-player"
      controls
      playsInline
      preload="metadata"
      poster={video.has_poster ? `/api/video-poster/${video.id}` : undefined}
      src={`/api/video-file/${video.id}`}
    />
    {video.caption && (
      <figcaption className="field-video-caption">{video.caption}</figcaption>
    )}
  </figure>
);

const FieldVideo = ({
  videos = [], locationId, fieldType, questId, maxVideos = DEFAULT_MAX_VIDEOS,
  onChanged, onEditCaption, onReorder, disabled,
  openVideoId, onOpenChange,
}) => {
  const [isBusy, setIsBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  // Set right before the picker opens: the id of the clip whose footage the
  // chosen file replaces, or null when the file joins the series.
  const replaceTargetRef = useRef(null);
  const pollTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(pollTimerRef.current), []);

  // Encoding runs server-side behind a task id, so the browser may be closed
  // mid-upload without losing the result; this just watches for the outcome.
  const pollTask = useCallback((taskId) => new Promise((resolve, reject) => {
    const tick = async () => {
      try {
        const res = await fetch(`/api/upload-video-task/${taskId}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Lost track of the upload task');
        const task = await res.json();
        if (task.status === 'done') return resolve(task);
        if (task.status === 'error') return reject(new Error(task.error || 'Processing failed'));
        pollTimerRef.current = setTimeout(tick, 2000);
      } catch (e) {
        reject(e);
      }
    };
    tick();
  }), []);

  // One file through the whole pipeline: upload, then wait for the encode.
  const uploadOne = async (file, replaceVideoId, label) => {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('locationId', locationId);
    formData.append('fieldType', fieldType);
    formData.append('questId', questId);
    formData.append('table', 'quests_test');
    if (replaceVideoId) formData.append('replaceVideoId', String(replaceVideoId));

    setStatus(`Uploading${label}…`);
    const res = await fetch('/api/upload-video', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload.error || 'Upload failed');

    setStatus(`Converting${label}…`);
    await pollTask(payload.taskId);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const replaceVideoId = replaceTargetRef.current;
    replaceTargetRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
    uploadFiles(files, replaceVideoId);
  };

  // Dropped files are always additions; replacing a clip goes through its button.
  const uploadFiles = async (files, replaceVideoId = null) => {
    if (files.length === 0 || isBusy || disabled) return;

    // A replacement takes exactly one file; an addition takes as many as fit.
    const room = replaceVideoId ? 1 : Math.max(0, maxVideos - videos.length);
    const queue = files.slice(0, room);
    if (queue.length === 0) {
      setError(`A field holds at most ${maxVideos} videos`);
      return;
    }
    const tooBig = queue.find((f) => f.size > MAX_UPLOAD_MB * 1024 * 1024);
    if (tooBig) {
      setError(`"${tooBig.name}" is larger than ${MAX_UPLOAD_MB} MB`);
      return;
    }

    setIsBusy(true);
    setError(null);
    try {
      // Sequential: the server encodes one clip per field at a time, and the
      // list refreshes after each so a finished clip shows while the next runs.
      for (let i = 0; i < queue.length; i++) {
        const label = queue.length > 1 ? ` ${i + 1}/${queue.length}` : '';
        await uploadOne(queue[i], replaceVideoId, label);
        if (onChanged) await onChanged();
      }
      if (files.length > queue.length) {
        const skipped = files.length - queue.length;
        setError(`Only ${maxVideos} videos fit on a field; ${skipped} file${skipped === 1 ? '' : 's'} skipped`);
      }
      setStatus('');
    } catch (err) {
      console.error('Video upload error:', err);
      setError(err.message);
      setStatus('');
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm('Delete this video?')) return;
    setIsBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/video-file/${video.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || 'Delete failed');
      }
      if (onOpenChange && openVideoId === video.id) onOpenChange(null);
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('Video delete error:', err);
      setError(err.message);
    } finally {
      setIsBusy(false);
    }
  };

  const openPicker = (replaceVideoId) => {
    replaceTargetRef.current = replaceVideoId || null;
    if (!fileInputRef.current) return;
    // A replacement is one file; an addition may be several at once.
    fileInputRef.current.multiple = !replaceVideoId;
    fileInputRef.current.click();
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= videos.length || !onReorder) return;
    const ids = videos.map((v) => v.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    onReorder(ids);
  };

  const metaOf = (video) => (
    [formatDuration(video.duration_sec), formatBytes(video.size_bytes)].filter(Boolean).join(' · ')
  );
  const canAdd = videos.length < maxVideos;

  const { isDragOver, dropProps } = useFileDrop(
    (files) => {
      const clips = files.filter((f) => f.type.startsWith('video/'));
      if (clips.length === 0) setError('Only video files can be dropped here');
      else uploadFiles(clips);
    },
    { disabled: disabled || isBusy || !canAdd },
  );

  return (
    <div className={isDragOver ? 'field-video is-drop-target' : 'field-video'} {...dropProps}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="video/*"
        multiple
        className="file-input"
      />

      {videos.map((video, i) => {
        const isOpen = openVideoId === video.id;
        return (
          <div className="field-video-row" key={video.id}>
            <button
              type="button"
              className={isOpen ? 'field-video-chip is-open' : 'field-video-chip'}
              onClick={() => onOpenChange && onOpenChange(isOpen ? null : video.id)}
              title={video.caption ? `${video.filename}\n“${video.caption}”` : video.filename}
            >
              {video.has_poster && (
                <img
                  key={video.uploaded_at}
                  className="field-video-thumb"
                  src={`/api/video-poster/${video.id}`}
                  alt=""
                />
              )}
              <span className="field-video-meta">🎬 {metaOf(video)}</span>
              <span className="field-video-caret">{isOpen ? '▲' : '▼'}</span>
            </button>
            {videos.length > 1 && (
              <>
                <button
                  type="button"
                  className="field-video-action"
                  onClick={() => move(i, -1)}
                  disabled={isBusy || disabled || i === 0}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="field-video-action"
                  onClick={() => move(i, 1)}
                  disabled={isBusy || disabled || i === videos.length - 1}
                  title="Move down"
                >
                  ↓
                </button>
              </>
            )}
            <button
              type="button"
              className={video.caption
                ? 'field-video-action field-video-action-caption has-caption'
                : 'field-video-action field-video-action-caption'}
              onClick={() => onEditCaption && onEditCaption(video)}
              disabled={isBusy || disabled}
              title={video.caption ? `Edit caption: “${video.caption}”` : 'Add caption'}
            >
              ✎
            </button>
            <button
              type="button"
              className="field-video-action"
              onClick={() => openPicker(video.id)}
              disabled={isBusy || disabled}
              title="Replace this video"
            >
              ⇄
            </button>
            <button
              type="button"
              className="field-video-action"
              onClick={() => handleDelete(video)}
              disabled={isBusy || disabled}
              title="Delete this video"
            >
              🗑️
            </button>
          </div>
        );
      })}

      <div className="field-video-row">
        {canAdd ? (
          <button
            type="button"
            className="upload-button field-video-add"
            onClick={() => openPicker(null)}
            disabled={isBusy || disabled}
            title="Pick files or drop them onto the video field"
          >
            {isBusy ? status || 'Working…' : '🎬 Add video'}
          </button>
        ) : (
          isBusy && <span className="field-video-status">{status || 'Working…'}</span>
        )}
        {videos.length > 0 && (
          <span className="field-video-count">{videos.length}/{maxVideos}</span>
        )}
      </div>

      {error && <div className="upload-error">{error}</div>}
    </div>
  );
};

export default FieldVideo;
